import { useEffect, useState } from 'react'
import { Contrast, X } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'

const DISMISS_KEY = 'pos-hc-suggested'

/**
 * One-time dismissible banner that nudges users on likely low-resolution
 * monitors (devicePixelRatio < 2) or those with an explicit OS-level
 * preference for more contrast (`prefers-contrast: more`) toward our
 * High Contrast theme. The suggestion is suppressed once dismissed
 * (per device, via localStorage) and once the user is already on the
 * high-contrast theme.
 *
 * Intentionally lightweight DOM — fixed banner at the top, two buttons,
 * no portal. The check runs on mount only; we don't react to live
 * devicePixelRatio changes (a user dragging the window between an
 * external 4K and a built-in panel is not the target case).
 */
export function HighContrastSuggestionBanner() {
  const { theme, setTheme } = useTheme()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (theme === 'high-contrast') return
    if (localStorage.getItem(DISMISS_KEY) === '1') return

    const lowDpi = window.devicePixelRatio < 2
    const prefersMoreContrast =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-contrast: more)').matches

    if (lowDpi || prefersMoreContrast) {
      setVisible(true)
    }
  }, [theme])

  // Auto-hide if the user switches to high-contrast through Settings while
  // the banner is open — no point keeping the prompt around.
  useEffect(() => {
    if (theme === 'high-contrast' && visible) {
      setVisible(false)
    }
  }, [theme, visible])

  if (!visible) return null

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setVisible(false)
  }

  const accept = () => {
    setTheme('high-contrast')
    localStorage.setItem(DISMISS_KEY, '1')
    setVisible(false)
  }

  // Top offset yields to the desktop update strip (F-FE-N47), which is z-[80]
  // and would otherwise clip this card's top edge. Resolves to 0px when no
  // update is pending, so the browser case is unchanged.
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-[calc(0.5rem_+_var(--desktop-update-banner-h))] left-1/2 z-[60] -translate-x-1/2 w-[calc(100%-1rem)] max-w-2xl rounded-lg border border-border bg-background shadow-lg"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <Contrast className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">
            Your display may render this app better in High Contrast mode
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            High Contrast uses heavier text and stronger colors, designed for older monitors.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={accept}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            Try High Contrast
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
